import { useCallback, useMemo, type ReactNode } from "react";
import { useRouter } from "@tanstack/react-router";
import { Button, ThrottledButton } from "web-design-system";
import { LoaderCircleIcon } from "lucide-react";
import { toast } from "sonner";
import { useKYCStatus, useSendKycEmail } from "creso-core/hooks";
import { KYC_VALIDATION_LINKS } from "creso-core/constants";
import { getKycState, isActiveClient } from "creso-core/utils";
import { Client } from "creso-core/types";
import { handleError } from "@/utils/error";

export type KycBanner = {
  description: ReactNode;
  actions: ReactNode;
};

// Returns the KYC banner content for the client's current KYC state, or null
// when KYC is complete. Callers frame it inside their own status cards.
export function useKycBanners(client: Client): KycBanner | null {
  const router = useRouter();
  const kycState = getKycState(client);

  const { refetch: checkKycStatus, isFetching: isFetchingKycStatus } =
    useKYCStatus(client.id ?? "", false);

  const onCheckKycStatus = useCallback(async () => {
    return checkKycStatus()
      .then((result) => {
        if (
          result.data?.kyc_status === client.kyc_status &&
          result.data?.kyc_status_remark === client.kyc_status_remark
        ) {
          toast.success("KYC check complete. No changes found.");
        } else {
          router.invalidate();
        }
      })
      .catch((error) => handleError(error, "Failed to check KYC status"));
  }, [checkKycStatus, client, router]);

  const { mutateAsync: sendKycLink } = useSendKycEmail();

  const onSendLink = useCallback(async () => {
    return sendKycLink(client.investment_account as string)
      .then(() => {
        toast.success("KYC link sent successfully");
      })
      .catch((error) => handleError(error, "Failed to send KYC link"));
  }, [sendKycLink, client.investment_account]);

  const onValidateKyc = useCallback(() => {
    if (client.kyc_kra_name) {
      window.open(
        KYC_VALIDATION_LINKS[
          client.kyc_kra_name.toLowerCase() as keyof typeof KYC_VALIDATION_LINKS
        ],
        "_blank"
      );
    } else {
      toast.error("KYC validation link not found");
    }
  }, [client.kyc_kra_name]);

  return useMemo(() => {
    const checkStatusButton = (
      <Button
        variant="outline"
        disabled={isFetchingKycStatus}
        onClick={onCheckKycStatus}
      >
        {isFetchingKycStatus && <LoaderCircleIcon className="animate-spin" />}
        Check status
      </Button>
    );

    const kycRequiredDescription = (
      <>
        <span className="font-semibold">Clients KYC required.&nbsp;</span>
        Ask them to complete application via SMS link or their App.
      </>
    );

    switch (kycState) {
      case "not-started":
        return {
          description: kycRequiredDescription,
          actions: (
            <>
              {checkStatusButton}
              <ThrottledButton
                throttle={10}
                beginOnMount={false}
                onClick={onSendLink}
              >
                Notify for KYC
              </ThrottledButton>
            </>
          ),
        };
      case "in-progress":
        return {
          description:
            "Client's KYC pending, ask them to complete verification via SMS link.",
          actions: (
            <>
              {checkStatusButton}
              <ThrottledButton
                throttle={10}
                beginOnMount={false}
                onClick={onSendLink}
              >
                Notify for KYC
              </ThrottledButton>
            </>
          ),
        };
      case "under-process":
        return {
          description: "Client’s KYC Submitted to KRA, will be validated soon.",
          actions: checkStatusButton,
        };
      case "must-validate":
        if (!isActiveClient(client)) return null;
        return {
          description:
            "KYC validation is pending. Please validate KYC to avoid transaction failures with new AMCs.",
          actions: (
            <>
              {checkStatusButton}
              <Button onClick={onValidateKyc}>Validate KYC</Button>
            </>
          ),
        };
      case "re-kyc":
        return {
          description: kycRequiredDescription,
          actions: (
            <>
              {checkStatusButton}
              <ThrottledButton
                throttle={10}
                beginOnMount={false}
                onClick={onSendLink}
              >
                Re-KYC
              </ThrottledButton>
            </>
          ),
        };
      case "failed":
        return {
          description: "KYC verification failed, ask them to apply again.",
          actions: <Button onClick={onSendLink}>Re-KYC</Button>,
        };
      case "complete":
      default:
        return null;
    }
  }, [
    kycState,
    isFetchingKycStatus,
    onCheckKycStatus,
    onSendLink,
    onValidateKyc,
  ]);
}
